<script setup>

import {computed, onMounted, ref, watch} from "vue";
import EditorTemplateModal from "@/components/common/EditorTemplateModal.vue";
import store from "@/vuex/store";
import editorTemplatesService from "@/api/services/editorTemplatesService";
import {ElNotification} from "element-plus";
import ConfirmModal from "@/components/common/ConfirmModal.vue";
import {getImageURL} from "@/helpers";

const props = defineProps({
  options: {type: Object, required: true},
  watermark_image: {type: [File, String, null]}
})

const emit = defineEmits(['templateChanged'])

const templates = ref([])
const selected_template = ref(null)
const showModal = ref(false)
const deleteConfirmModal = ref(false)
const applyModalConfirm = ref(false)
const updateModalConfirm = ref(false)
const tenant_id = computed(() => store.getters["editor/getTenantId"])
const filteredOptions = computed(() => {
  let _options = {...props.options}
  delete _options.duration
  delete _options.video_duration
  delete _options.video_url
  delete _options.trim_interval
  delete _options.watermark_interval
  delete _options.cut_out_interval
  return _options
})

onMounted(async () => {
  await fetchTemplates()
})

async function fetchTemplates() {
  let res = await editorTemplatesService.getTemplates(tenant_id.value)
  if (res.data.success) {
    templates.value = res.data.templates
  }
}

async function uploadImage() {
  if (props.watermark_image === null) return null
  let res = await store.dispatch('editor/uploadImage', props.watermark_image)
  if (res) return res.data
  else return null
}

async function createTemplate(name) {
  let image = await uploadImage()

  let res = await editorTemplatesService.createTemplate(tenant_id.value, {
    options: {...filteredOptions.value, watermark_image: image?.image, watermark_image_url: getImageURL(image?.image)},
    name: name
  })
  if (res.status === 200) {
    templates.value.push(res.data)
    ElNotification.success({
      message: `Template ${name} was successfully created.`,
      offset: 50
    })
  }
  showModal.value = false
}

async function updateTemplate() {
  let template = templates.value.find(t => t.id === selected_template.value)
  let image = await uploadImage()

  let res = await editorTemplatesService.updateTemplate(tenant_id.value, {
    options: {...filteredOptions.value, watermark_image: image?.image, watermark_image_url: getImageURL(image?.image)},
    name: template.name,
    id: template.id,
  })
  if (res.status === 200) {
    templates.value.splice(templates.value.findIndex(t => t.id === res.data.id), 1, res.data)
    ElNotification.success({
      message: `Template ${template.name} was successfully updated.`,
      offset: 50
    })
  }
  updateModalConfirm.value = false
}

async function removeTemplate() {
  let res = await editorTemplatesService.deleteTemplate(selected_template.value, tenant_id.value)
  if (res.status === 200) {
    templates.value = templates.value.filter(t => t.id !== selected_template.value)
    selected_template.value = null
  }
  deleteConfirmModal.value = false
}

function applyTemplate() {
  let template = templates.value.find(t => t.id === selected_template.value)
  applyModalConfirm.value = false
  emit('templateChanged', template.options)
}

</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="templates.length" class="flex gap-2 items-center justify-between">
      <span class="text-dark_blue text-[12px] font-bold">Templates</span>
     <div  v-if="selected_template" class="flex gap-2 ">
       <el-tooltip content="Apply selected template" :enterable="false" :hide-after="0">
         <button class="w-6 h-6 bg-green-450 text-white rounded-[4px] text-[12px]"
                 @click="applyModalConfirm = true">
           <i class="fa fa-check"></i>
         </button>
       </el-tooltip>
       <el-tooltip content="Delete selected template" :enterable="false" :hide-after="0">
         <button class="w-6 h-6 bg-red-400 text-white rounded-[4px] text-[12px]"
                 @click="deleteConfirmModal = true">
           <i class="fa fa-trash"></i>
         </button>
       </el-tooltip>
     </div>
    </div>
    <el-select v-if="templates.length" v-model="selected_template" placeholder="Select template">
      <el-option v-for="temp in templates" :value="temp.id" :label="temp.name"/>
    </el-select>


    <div class="flex gap-2">
      <button
        v-if="selected_template"
        class=" h-8 w-1/2 border text-blue-450 border-blue-450 text-xs rounded font-bold "
        @click="updateModalConfirm = true"
      >
        Update template
      </button>
      <button
        class="text-white h-8 w-1/2 bg-blue-450 rounded font-bold text-xs ml-auto"
        @click="showModal = true"
      >
        Save as template
      </button>
    </div>
  </div>

  <EditorTemplateModal :isOpen="showModal" @close="showModal = false" @save="createTemplate"/>

  <ConfirmModal :isOpen="deleteConfirmModal"
                content="Are you sure you want to delete selected template?"
                @close="deleteConfirmModal = false" @cancel="deleteConfirmModal = false" @confirm="removeTemplate"/>

  <ConfirmModal :isOpen="applyModalConfirm"
                content="Are you sure you want to apply selected template?"
                @close="applyModalConfirm = false" @cancel="applyModalConfirm = false" @confirm="applyTemplate"/>

  <ConfirmModal :isOpen="updateModalConfirm"
                content="Are you sure you want to update selected template?"
                @close="updateModalConfirm = false" @cancel="updateModalConfirm = false" @confirm="updateTemplate"/>
</template>
